import { useEffect, useRef } from 'react'

import { useGetTrackRightsSplitsQuery } from '@disco/state-management/api/rights-splits'
import {
  getTrackRightsState,
  initBulkEdit,
  isTrackDirty,
  loadTrackRightsFromRightsApi,
  type SplitsKey,
} from '@disco/state-management/reducers/rightsManagement'

import { useDispatch, useSelector } from 'hooks/use-redux'

// Both tabs share this request, so its lifecycle belongs to their container.
export function useLoadTrackRights(
  trackId: number | null,
  isBulkMode: boolean,
  splitsKey: SplitsKey,
) {
  const dispatch = useDispatch()
  const isTrackDataLoaded = useSelector(
    state => getTrackRightsState(state, splitsKey) !== undefined,
  )
  const hasUnsavedChanges = useSelector(state => isTrackDirty(state, splitsKey))
  const loadedTrackId = useRef<number | null>(null)
  const { currentData, isError, refetch } = useGetTrackRightsSplitsQuery(
    { trackId: trackId! },
    { skip: isBulkMode || trackId === null },
  )

  useEffect(() => {
    loadedTrackId.current = null
  }, [trackId])

  useEffect(() => {
    if (isBulkMode) {
      if (!isTrackDataLoaded) dispatch(initBulkEdit())
      return
    }
    if (trackId === null || loadedTrackId.current === trackId) return
    if (isTrackDataLoaded && hasUnsavedChanges) {
      loadedTrackId.current = trackId
      return
    }
    // Initialize from this track's cached data even if the latest refetch failed,
    // so reopening never unlocks an empty editor (currentData is arg-specific).
    if (currentData === undefined) return
    dispatch(
      loadTrackRightsFromRightsApi({ trackId, rightsSplitChains: currentData }),
    )
    loadedTrackId.current = trackId
  }, [
    dispatch,
    trackId,
    isBulkMode,
    isTrackDataLoaded,
    hasUnsavedChanges,
    currentData,
  ])

  // Ready once this track's data is present. currentData (not isSuccess) survives
  // a failed refetch and resets on track change.
  const isSingleTrack = !isBulkMode && trackId !== null
  const hasData = currentData !== undefined
  return {
    isInitialLoadComplete: isBulkMode || (isSingleTrack && hasData),
    // The server's saved rights, unlike the Redux splits, never include drafts
    savedChains: currentData,
    isInitialLoading: isSingleTrack && !hasData && !isError,
    isInitialLoadError: isSingleTrack && !hasData && isError,
    isRefetchError: isSingleTrack && hasData && isError,
    refetchTrackRights: refetch,
  }
}
